import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslate } from '../hooks/useTranslate';
import { getButtonClasses, getButtonStyles } from '../constants';

interface DeleteConfirmationModalProps {
    isOpen: boolean;
    onClose: () => void;
    onConfirm: () => void;
    entityName: string;
    /** Optional custom title (defaults to 'Delete Item') */
    title?: string;
    /** Optional custom confirmation button label (defaults to 'Delete Permanently') */
    confirmLabel?: string;
    /** Optional description override. {entityName} placeholder will be replaced automatically if present. */
    description?: string;
    /** Optional secondary action label (e.g. 'Remove from Folder'). When provided, renders a second button below primary. */
    secondaryLabel?: string;
    /** Secondary action handler. Only used if secondaryLabel is provided. */
    onSecondary?: () => void;
}

const DeleteConfirmationModal: React.FC<DeleteConfirmationModalProps> = ({
    isOpen,
    onClose,
    onConfirm,
    entityName,
    title,
    confirmLabel,
    description,
    secondaryLabel,
    onSecondary
}) => {
    const { t } = useTranslate();
    if (!isOpen) return null;

    const resolvedTitle = title || t('Delete Item');
    const resolvedConfirmLabel = confirmLabel || t('Delete Permanently');
    const computedDescription = (description || t('This will permanently delete "{entityName}". This action cannot be undone.'))
        .replace('{entityName}', entityName);

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.stopPropagation();
            onClose();
        }
    };

    return createPortal(
        <div
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-entity-title"
            className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
            onClick={() => { }} // Disabled backdrop click to prevent accidental closes
            onKeyDown={handleKeyDown}
            tabIndex={-1}
        >
            <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
            <div className="relative bg-white rounded-2xl w-full max-w-md shadow-xl border border-gray-200">
                <button
                    onClick={onClose}
                    aria-label="Close delete confirmation"
                    className="absolute top-3 right-3 inline-flex items-center justify-center w-8 h-8 rounded-full text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                    <svg className="w-4 h-4" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} fill="none" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M18 6L6 18" />
                        <path d="M6 6l12 12" />
                    </svg>
                </button>
                <div className="p-6">
                    <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full">
                        <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                    </div>
                    <h3 id="delete-entity-title" className="text-lg font-semibold text-gray-900 text-center mb-2">
                        {resolvedTitle}
                    </h3>
                    <p className="text-sm text-gray-600 text-center mb-6">
                        {computedDescription.split(/(".*?"|<span.*?>.*?<\/span>)/).map((segment, idx) => {
                            // Simple handling: the description already contains quotes around entityName
                            return <React.Fragment key={idx}>{segment}</React.Fragment>
                        })}
                    </p>
                    <div className="space-y-3">
                        <button
                            onClick={() => {
                                onConfirm();
                                onClose();
                            }}
                            className="w-full flex items-center justify-center px-6 py-4 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group"
                            style={{
                                background: 'linear-gradient(to right, #dc2626, #b91c1c, #991b1b)',
                                boxShadow: '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(220, 38, 38, 0.6), 0 12px 15px -6px rgba(220, 38, 38, 0.4)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = '';
                                e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(220, 38, 38, 0.4), 0 8px 10px -6px rgba(220, 38, 38, 0.3)';
                            }}
                        >
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                            {resolvedConfirmLabel}
                        </button>
                        {secondaryLabel && onSecondary && (
                            <button
                                onClick={() => {
                                    onSecondary();
                                    onClose();
                                }}
                                className="w-full flex items-center justify-center px-6 py-4 text-white font-semibold rounded-full shadow-2xl hover:shadow-3xl transform hover:scale-110 transition-all duration-300 group"
                                style={{
                                    background: 'linear-gradient(to right, #6b7280, #4b5563, #374151)',
                                    boxShadow: '0 10px 25px -5px rgba(107, 114, 128, 0.4), 0 8px 10px -6px rgba(107, 114, 128, 0.3)'
                                }}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'scale(1.15) translateY(-3px)';
                                    e.currentTarget.style.boxShadow = '0 15px 35px -5px rgba(107, 114, 128, 0.6), 0 12px 15px -6px rgba(107, 114, 128, 0.4)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = '';
                                    e.currentTarget.style.boxShadow = '0 10px 25px -5px rgba(107, 114, 128, 0.4), 0 8px 10px -6px rgba(107, 114, 128, 0.3)';
                                }}
                            >
                                {secondaryLabel}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DeleteConfirmationModal;